/**
 * AiTextToDataBridge — host-side subscriber for the `ai-open-in-drawer`
 * legacy fallback (see AiDocumentCreatorBridge's `open-in-drawer` handler).
 *
 * Why this exists:
 *   Before the drafts pipeline (`/v1/drafts`) existed, the compact docs tab
 *   inside the AI chat iframe asked the host to open the ERP's native
 *   text-to-data smart drawer (`TextToDataDrawerAi`, normally mounted
 *   per-page e.g. in the orders datasheet) so the user could review/finish
 *   the extraction there. That event (`ai-open-text-to-data`) was dispatched
 *   but nothing listened globally — this component is that listener, so the
 *   fallback works from *any* page, not just the datasheet.
 *
 *   The MODERN primary flow (hostBridge.openDraft → DraftsDrawer inside the
 *   chat iframe itself) does not go through this component at all. This
 *   bridge only matters for legacy jobs / when the drafts endpoint fails.
 *
 * Limitations:
 *   `TextToDataDrawerAi` only supports pre-selecting `initialProcess`
 *   ('order' | 'supplier_invoice') and `initialEntityId` — it has no prop
 *   for pre-loading already-extracted product lines, so "pre-filled" here
 *   means "opened on the right process/client", not "lines already typed
 *   in". The user still pastes/extracts inside the native drawer.
 *
 * Mounting:
 *   Mounted once in MainLayout alongside <AiDocumentCreatorBridge>.
 */
import React, { useCallback, useEffect, useState } from 'react';

import { TextToDataDrawerAi, useTextToDataAiAvailability } from '../../../features/text-to-data-ai';
import type { TextToDataProcess } from '../../../features/text-to-data-ai/types';

interface IncomingTextToDataPayload {
    process?: string;
    dateIso?: string;
    entity?: { id?: string | number | null };
}

/** Map the docs-tab `process` enum to the drawer's narrower vocabulary.
 *  Anything supplier-side other than an invoice still opens on 'order' —
 *  better than not opening at all; the user picks the right process inside
 *  the drawer's own toggle. */
function resolveProcess(process?: string): TextToDataProcess {
    return process === 'supplier_invoice' || process === 'supplier_delivery'
        ? 'supplier_invoice'
        : 'order';
}

function parseDate(dateIso?: string): Date {
    if (dateIso) {
        const d = new Date(dateIso);
        if (!Number.isNaN(d.getTime())) return d;
    }
    return new Date();
}

const AiTextToDataBridge = () => {
    const availability = useTextToDataAiAvailability();
    const [payload, setPayload] = useState<IncomingTextToDataPayload | null>(null);

    useEffect(() => {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent).detail as IncomingTextToDataPayload | undefined;
            setPayload(detail || {});
        };
        window.addEventListener('ai-open-text-to-data', handler as EventListener);
        return () => window.removeEventListener('ai-open-text-to-data', handler as EventListener);
    }, []);

    const close = useCallback(() => setPayload(null), []);

    // Feature disabled for this user/token (no product_extraction scope) —
    // never mount the drawer even if the iframe fires the event.
    if (!payload || !availability.available) return null;

    return (
        <TextToDataDrawerAi
            open
            onClose={close}
            defaultDate={parseDate(payload.dateIso)}
            initialProcess={resolveProcess(payload.process)}
            initialEntityId={payload.entity?.id ?? null}
        />
    );
};

export default AiTextToDataBridge;
